import { BelongsTo, Model } from 'spraypaint';
import ApplicationRecord from '../ApplicationRecord';
import User from './User';

/**
 * A staff member sending a bidder a fresh verification email (P5-03).
 *
 * Deliberately NOT the same endpoint as `EmailVerificationResend`, which is the
 * bidder's own self-service route: that one is unauthenticated and reports
 * generic success whatever happens, so it cannot be used to discover which
 * addresses have accounts. This one names a user by id, requires
 * `users.change_user`, and *does* say what happened - it answers to a colleague
 * who needs to know whether the email actually went.
 *
 * Two refusals are normal outcomes rather than faults, and both must be shown:
 *
 * - `400` with `code: "already_verified"` - the account has nothing left to
 *   confirm.
 * - `429` - throttled. The rate-limit bucket is **shared with the bidder's own
 *   resends** by decision: the limit is not about trusting staff, it is about
 *   never letting this become a bulk mailer. So a staff resend can be refused
 *   because the bidder clicked resend a minute ago. The flip side is that a
 *   staff resend starts the bidder's cooldown.
 */
@Model()
export default class StaffVerificationResend extends ApplicationRecord {
  static jsonapiType = 'staff-verification-resends';

  /**
   * Send to the given user.
   *
   * `with: 'user.id'` sends the relationship as a bare identifier, which is all
   * the server reads - the same shape `BidEdit` uses. Nothing is echoed back:
   * the resource is write-only, so success is the 201 itself.
   */
  static async send(user: User): Promise<boolean> {
    const resend = new StaffVerificationResend();
    resend.user = user;
    return resend.save({ with: 'user.id' });
  }

  // Write-only: who to send to. Never present in a response.
  @BelongsTo() user: User;
}
